import { useState, useEffect, useCallback } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { FoundationRole, FoundationState, SignupResult, CreatorSignupData, DeveloperSignupData } from '../types';
import { FOUNDATION_CAPS, creatorSignupSchema, developerSignupSchema } from '../types';

/**
 * Hook to check foundation program availability
 */
export function useFoundationAvailability(
  supabase: SupabaseClient,
  role: FoundationRole
): FoundationState & { refresh: () => Promise<void> } {
  const [state, setState] = useState<FoundationState>({
    status: 'checking',
    spotsRemaining: null,
    totalSpots: FOUNDATION_CAPS[role],
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    
    try {
      const { data, error } = await supabase
        .from('founding_programs')
        .select('seat_target, seat_used, state')
        .eq('role', role)
        .maybeSingle();

      if (error) throw error;

      const seatTarget = data?.seat_target ?? FOUNDATION_CAPS[role];
      const seatUsed = data?.seat_used ?? 0;
      const remaining = Math.max(0, seatTarget - seatUsed);

      setState({
        status: remaining > 0 ? 'available' : 'full',
        spotsRemaining: remaining,
        totalSpots: seatTarget,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState(s => ({
        ...s,
        status: 'available', // Fail open to allow signups
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to check availability',
      }));
    }
  }, [supabase, role]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}

/**
 * Hook to handle foundation signup submission
 */
export function useFoundationSignup(supabase: SupabaseClient, role: FoundationRole) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SignupResult | null>(null);

  const submit = useCallback(async (
    data: CreatorSignupData | DeveloperSignupData
  ): Promise<SignupResult> => {
    setSubmitting(true);
    setResult(null);

    try {
      // Validate input
      const schema = role === 'creator' ? creatorSignupSchema : developerSignupSchema;
      const validated = schema.parse(data);

      // Submit to edge function
      const { data: response, error } = await supabase.functions.invoke('secure-waitlist-signup', {
        body: {
          ...validated,
          role,
          feature: `foundation_${role}`,
          signup_source: `foundation_${role}`,
          is_foundation: true,
          metadata: role === 'developer' ? {
            experience_level: (validated as DeveloperSignupData).experience_level,
            primary_skills: (validated as DeveloperSignupData).primary_skills,
            github_url: (validated as DeveloperSignupData).github_url,
            linkedin_url: (validated as DeveloperSignupData).linkedin_url,
            portfolio_url: (validated as DeveloperSignupData).portfolio_url,
            motivation: (validated as DeveloperSignupData).motivation,
          } : {
            platforms: (validated as CreatorSignupData).platforms,
            content_types: (validated as CreatorSignupData).content_types,
            audience_size: (validated as CreatorSignupData).audience_size,
            automation_interests: (validated as CreatorSignupData).automation_interests,
            current_challenges: (validated as CreatorSignupData).current_challenges,
          },
        },
      });

      if (error) {
        const isDuplicate = error.message?.toLowerCase().includes('duplicate') ||
                           error.message?.toLowerCase().includes('already');
        return {
          success: false,
          errorCode: isDuplicate ? 'DUPLICATE_EMAIL' : 'SERVER_ERROR',
          message: isDuplicate ? 'This email is already registered' : error.message,
        };
      }

      const res = response as { success?: boolean; ok?: boolean; message?: string; error_code?: string };
      
      if (res?.error_code === 'DUPLICATE_EMAIL') {
        return { success: false, errorCode: 'DUPLICATE_EMAIL', message: 'This email is already registered' };
      }

      if (res?.success || res?.ok) {
        const successResult: SignupResult = {
          success: true,
          message: res.message || `Welcome to the Foundation ${role === 'creator' ? 'Creator' : 'Developer'} program!`,
        };
        setResult(successResult);
        return successResult;
      }

      return { success: false, errorCode: 'SERVER_ERROR', message: res?.message || 'Signup failed' };
    } catch (err) {
      if (err instanceof Error && err.name === 'ZodError') {
        return { success: false, errorCode: 'VALIDATION', message: 'Please check your input' };
      }
      return { success: false, errorCode: 'SERVER_ERROR', message: 'An unexpected error occurred' };
    } finally {
      setSubmitting(false);
    }
  }, [supabase, role]);

  const reset = useCallback(() => {
    setResult(null);
    setSubmitting(false);
  }, []);

  return { submit, submitting, result, reset };
}
